// EnumPillSelect.jsx

import React, { useCallback, useMemo } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import {
  getEnumOptionColor,
  getEnumOptionLabel,
  getOptionLabel,
  resolveEnumOptionsForField,
} from 'utilities/EnumOptions';
import { buildNodeFieldPatch } from 'utilities/NodeFieldData';

// CSS
import './EnumPillSelect.css';

// Components
import Pill from 'components/pill/Pill';
import SelectInput from 'components/selectinput/SelectInput';

/**
 * Colored, auto-width enum chip: view + change in one control.
 *
 * @param {object} field - Schema field (enum).
 * @param {string} value - Current enum value.
 * @param {object} node - Node being edited.
 * @param {string} [typeKey] - Node type key for patches.
 * @param {boolean} [canEdit=false] - When true, renders SelectInput pill.
 * @param {function} [onNodeChange] - (nodeKey, patch) => void.
 * @param {string} [placeholder='Select…'] - Empty / clear label (e.g. "Status…").
 * @param {string} [language='en'] - Language for option labels.
 * @param {string} [fontSize='12px'] - Trigger label size (Peek compact vs Node/Workspace).
 */
const EnumPillSelect = React.memo(({
  field = null,
  value = '',
  node = null,
  typeKey = '',
  canEdit = false,
  onNodeChange = null,
  placeholder = 'Select…',
  language = 'en',
  fontSize = '12px',
}) => {
  const { theme } = useTheme();

  const resolvedTypeKey = String(typeKey || node?.type || '').trim().toUpperCase();
  const normalizedValue = value == null ? '' : String(value).trim();

  const enumOptions = useMemo(
    () => resolveEnumOptionsForField(field, language),
    [field, language],
  );

  const options = useMemo(() => {
    const next = enumOptions
      .map((option) => {
        const optionValue = String(option?.value ?? '').trim();
        if (!optionValue) return null;
        return {
          value: optionValue,
          label: getOptionLabel(option, language) || optionValue,
          iconKey: String(option?.iconKey || '').trim(),
          color: typeof option?.color === 'string' ? option.color.trim() : '',
          note: String(option?.note || '').trim(),
        };
      })
      .filter(Boolean);

    if (field?.allowClear === false) return next;
    return [{ value: '', label: placeholder }, ...next];
  }, [enumOptions, field?.allowClear, language, placeholder]);

  const color = useMemo(
    () => getEnumOptionColor(enumOptions, normalizedValue),
    [enumOptions, normalizedValue],
  );

  const label = useMemo(
    () => (normalizedValue
      ? (getEnumOptionLabel(enumOptions, normalizedValue, language) || normalizedValue)
      : ''),
    [enumOptions, language, normalizedValue],
  );

  const handleChange = useCallback((event) => {
    if (!canEdit || typeof onNodeChange !== 'function' || !node?.key || !field?.name) return;
    const nextValue = String(event?.target?.value ?? event ?? '').trim();
    const patchField = {
      ...field,
      name: field.name,
      source: field.source || resolvedTypeKey.toLowerCase(),
      type: field.type || 'enum',
    };
    const patch = buildNodeFieldPatch(node, patchField, resolvedTypeKey, nextValue);
    if (!patch) return;
    onNodeChange(node.key, patch);
  }, [canEdit, field, node, onNodeChange, resolvedTypeKey]);

  const hasColor = Boolean(color && String(color).trim());
  const foreground = hasColor
    ? (theme.highlightForeground || '#fff')
    : (normalizedValue ? theme.foreground : theme.foregroundMuted);
  // Match Node type pills: Pill uses inline backgroundColor={theme.panel}.
  const background = hasColor ? color : (theme.panel || 'var(--panel)');

  if (canEdit) {
    const displayText = label || placeholder;
    const stopRowActivation = (event) => {
      event.stopPropagation();
    };
    return (
      <div
        className={`enum-pill-select${hasColor ? ' has-color' : ''}${normalizedValue ? '' : ' is-empty'}`}
        style={{
          backgroundColor: background,
          color: foreground,
          '--enum-pill-bg': background,
          '--enum-pill-fg': foreground,
        }}
        onClick={stopRowActivation}
        onMouseDown={stopRowActivation}
      >
        <SelectInput
          value={normalizedValue}
          options={options}
          placeholder={placeholder}
          showIcons={false}
          width="fit-content"
          size={Math.max(displayText.length, 5)}
          endIconColor={hasColor ? foreground : theme.foregroundMuted}
          iconSize={18}
          style={{
            width: 'auto',
            fontSize,
            fontWeight: 400,
            paddingLeft: 12,
            paddingRight: 28,
            paddingTop: 5,
            paddingBottom: 5,
            background: 'transparent',
            backgroundColor: 'transparent',
            borderColor: 'transparent',
            color: foreground,
          }}
          onChange={handleChange}
        />
      </div>
    );
  }

  if (!label) return null;

  return (
    <Pill
      label={label}
      color={hasColor ? color : theme.panel}
      labelColor={foreground}
    />
  );
});

EnumPillSelect.displayName = 'EnumPillSelect';

export default EnumPillSelect;
